import React, { useState, useEffect, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import InputField from "../../Common/Form/InputField";
import LegalAgreement from "../Legal/LegalAgreement";
import LegalFooter from "../Legal/LegalFooter";
import {
  INITIAL_LEGAL_AGREEMENT,
  clearLegalErrors,
  legalAgreementPayload,
  validateLegalAgreement,
} from "../Legal/legalRules";
import SelectField from "../../Common/Form/SelectField";
import { registerUser } from "../../../services/auth.services";
import { showSnackBar } from "../../../store/common/snackBarSlice";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Countries from "../../../data/countries";
import { useTranslation } from "react-i18next";
import { useFormat } from "../../../i18n/format";

// What to tell the user about a required field left empty.
const REQUIRED_MESSAGES = {
  warehousename: "Enter the warehouse name",
  warehouseaddress: "Enter the warehouse address",
  city: "Enter the city",
  country: "Choose the warehouse country",
  state: "Enter the state or province",
  passcode: "Enter the postal code",
  business_name: "Enter your business name",
  industry_type: "Enter your industry type",
  username: "Choose a username",
  firstname: "Enter your first name",
  lastname: "Enter your last name",
  email: "Enter your email address",
  password: "Create a password",
};

const UserIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const BriefcaseIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0H8m8 0v2a2 2 0 01-2 2H10a2 2 0 01-2-2V6"
    />
  </svg>
);

const EnvelopeIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const AddressIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const BackIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 19l-7-7m0 0l7-7m-7 7h18"
    />
  </svg>
);

// Loading Button Component
const BtnLoader = () => (
  <div className="flex justify-center items-center">
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
  </div>
);

const WarehouseForm = ({ onBack }) => {
  const { t } = useTranslation(["auth", "common"]);
  const { countryOptions } = useFormat();
  const [formData, setFormData] = useState({
    warehousename: "",
    warehouseaddress: "",
    city: "",
    country: "",
    state: "",
    passcode: "",
    business_name: "",
    industry_type: "",
    username: "",
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    role: "warehouseUser",
  });

  const [confirmPassword, setConfirmPassword] = useState("");
  const [legal, setLegal] = useState(INITIAL_LEGAL_AGREEMENT);
  const [verified, setVerified] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Refs for each field
  const warehousenameRef = useRef(null);
  const warehouseaddressRef = useRef(null);
  const cityRef = useRef(null);
  const countryRef = useRef(null);
  const stateRef = useRef(null);
  const passcodeRef = useRef(null);
  const businessNameRef = useRef(null);
  const industryTypeRef = useRef(null);
  const usernameRef = useRef(null);
  const firstnameRef = useRef(null);
  const lastnameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const recaptchaRef = useRef(null);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setTimeout(() => {
        validateSingleField(field, value);
      }, 100);
    }
  };

  const handleConfirmPasswordChange = (value) => {
    setConfirmPassword(value);
    if (errors.confirmPassword && formData.password === value) {
      setErrors((prev) => ({ ...prev, confirmPassword: "" }));
    }
  };

  const handleLegalChange = (next) => {
    setLegal(next);
    setErrors((prev) => clearLegalErrors(prev, next));
  };

  const validateSingleField = (field, value) => {
    let isValid = false;

    switch (field) {
      case "email":
      case "username":
        isValid = /^[a-zA-Z0-9_]{3,20}$/.test(value);
        break;
      case "password":
        isValid = value && value.length >= 8;
        break;
      case "confirmPassword":
        isValid = value && value === formData.password;
        break;
      default:
        isValid = value && value.toString().trim() !== "";
    }

    if (isValid) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const requiredFields = [
      "warehousename",
      "warehouseaddress",
      "city",
      "country",
      "state",
      "passcode",
      "business_name",
      "industry_type",
      "username",
      "firstname",
      "lastname",
      "email",
      "password",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].toString().trim() === "") {
        newErrors[field] = REQUIRED_MESSAGES[field]
          ? t(`register.required.warehouse.${field}`, { defaultValue: REQUIRED_MESSAGES[field] })
          : t("validation.fillInField");
      }
    });

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("validation.emailValid");
    }

    if (formData.username && !/^[a-zA-Z0-9_]{3,20}$/.test(formData.username)) {
      newErrors.username =
        t("validation.usernameRule");
    }

    if (formData.password && formData.password.length < 8) {
      newErrors.password = t("validation.passwordMinLong");
    }

    if (formData.password !== confirmPassword) {
      newErrors.confirmPassword = t("validation.passwordsMismatch");
    }

    Object.assign(newErrors, validateLegalAgreement(legal));

    if (!verified) {
      newErrors.recaptcha = t("validation.recaptchaRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validateForm();

    if (!isValid) {
      scrollToFirstError();
      return;
    }

    const payload = {
      name: formData.warehousename,
      address: formData.warehouseaddress,
      city: formData.city,
      country: formData.country,
      state: formData.state,
      passcode: formData.passcode,
      role: "warehouseUser",
      ...legalAgreementPayload(legal),
      user: {
        username: formData.username,
        firstname: formData.firstname,
        lastname: formData.lastname,
        business_name: formData.business_name,
        industry_type: formData.industry_type,
        email: formData.email,
        password: formData.password,
        zip: formData.passcode,
        city: formData.city,
        country: formData.country,
        province: formData.state,
        address: formData.warehouseaddress,
      },
    };

    try {
      setLoading(true);
      const res = await registerUser(payload);
      if (res.error) {
        let message = "";
        if (res?.error?.response?.data?.username) {
          message = res?.error?.response?.data?.username[0];
        } else if (res?.error?.response?.data?.email) {
          message = res?.error?.response?.data?.email[0];
        } else {
          message =
            res?.error?.response?.data?.message ?? "Something went wrong";
        }
        dispatch(
          showSnackBar({
            text: message,
            severity: "error",
          })
        );
        setLoading(false);
      } else {
        dispatch(
          showSnackBar({
            text: res?.data?.message,
            severity: "success",
          })
        );
        navigate("/welcome", {
          state: {
            username: res?.data?.result?.user?.username,
            email: res?.data?.result?.user?.email,
          },
        });
        setLoading(false);
      }
    } catch (error) {
      dispatch(
        showSnackBar({
          text: error?.message || t("common:messages.somethingWentWrong"),
          severity: "error",
        })
      );
      setLoading(false);
    }
  };

  const onRecaptchaChange = (value) => {
    setVerified(!!value);
    if (errors.recaptcha && value) {
      setErrors((prev) => ({ ...prev, recaptcha: "" }));
    }
  };

  const scrollToFirstError = () => {
    const errorFields = Object.keys(errors);
    if (errorFields.length === 0) return;

    const fieldRefs = {
      warehousename: warehousenameRef,
      warehouseaddress: warehouseaddressRef,
      city: cityRef,
      country: countryRef,
      state: stateRef,
      passcode: passcodeRef,
      business_name: businessNameRef,
      industry_type: industryTypeRef,
      username: usernameRef,
      firstname: firstnameRef,
      lastname: lastnameRef,
      email: emailRef,
      password: passwordRef,
      recaptcha: recaptchaRef,
    };

    const firstErrorField = errorFields.find((field) => errors[field]);

    if (firstErrorField && fieldRefs[firstErrorField]?.current) {
      fieldRefs[firstErrorField].current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      if (fieldRefs[firstErrorField].current.focus) {
        fieldRefs[firstErrorField].current.focus();
      }
    }
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => {
              onBack ? onBack() : navigate("/Register");
            }}
            className="absolute top-5 left-5 md:static inline-flex items-center gap-1 text-sm font-medium text-white bg-brand hover:bg-brand-hover px-3 py-1.5 rounded-md transition-colors duration-200"
          >
            <BackIcon className="h-4 w-4" />
            {t("login.back")}
          </button>
          <div className="text-center flex-grow pt-4 md:pt-0">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-brand rounded-full mb-2">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-ui-text mb-1">
              {t("register.warehouse.title")}
            </h1>
            <p className="text-sm text-ui-text-muted">
              {t("register.warehouse.subtitle")}
            </p>
          </div>
          <div className="w-20"></div> {/* Spacer for balance */}
        </div>

        {/* Form Container */}
        <div className="bg-ui-surface border border-ui-border rounded-lg overflow-hidden">
          <div className="bg-brand p-4">
            <h2 className="text-lg font-bold text-white text-center">
              {t("register.getStarted")}
            </h2>
          </div>

          <div className="p-6 md:p-8">
            <div className="space-y-3">
              {/* Warehouse Information Section */}
              <div className="space-y-1">
                <div className="flex items-center space-x-2 mb-1">
                  <div className="flex-shrink-0 w-6 h-6 bg-brand rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-xs">1</span>
                  </div>
                  <h3 className="text-lg font-bold text-ui-text relative top-1">
                    {t("register.sections.warehouse")}
                  </h3>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <InputField
                    label={t("register.fields.warehouseName")}
                    value={formData.warehousename}
                    ref={warehousenameRef}
                    onChange={(e) =>
                      handleFieldChange("warehousename", e.target.value)
                    }
                    error={errors.warehousename}
                    placeholder={t("register.fields.warehouseNamePh")}
                    icon={<BriefcaseIcon />}
                    required
                  />
                  <InputField
                    label={t("register.fields.warehouseAddress")}
                    value={formData.warehouseaddress}
                    ref={warehouseaddressRef}
                    onChange={(e) =>
                      handleFieldChange("warehouseaddress", e.target.value)
                    }
                    error={errors.warehouseaddress}
                    placeholder={t("register.fields.warehouseAddressPh")}
                    icon={<AddressIcon />}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <SelectField
                    label={t("register.fields.country")}
                    value={formData.country}
                    ref={countryRef}
                    onChange={(e) =>
                      handleFieldChange("country", e.target.value)
                    }
                    options={countryOptions(Countries)}
                    error={errors.country}
                    placeholder={t("register.fields.warehouseCountryPh")}
                    required
                  />
                  <InputField
                    label={t("register.fields.city")}
                    value={formData.city}
                    ref={cityRef}
                    onChange={(e) => handleFieldChange("city", e.target.value)}
                    error={errors.city}
                    placeholder={t("register.fields.cityPh")}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <InputField
                    label={t("register.fields.stateProvince")}
                    value={formData.state}
                    ref={stateRef}
                    onChange={(e) => handleFieldChange("state", e.target.value)}
                    error={errors.state}
                    placeholder={t("register.fields.stateProvincePh")}
                    required
                  />
                  <InputField
                    label={t("register.fields.postalCode")}
                    value={formData.passcode}
                    ref={passcodeRef}
                    onChange={(e) =>
                      handleFieldChange("passcode", e.target.value)
                    }
                    error={errors.passcode}
                    placeholder={t("register.fields.postalCodePh")}
                    required
                  />
                </div>
              </div>

              {/* Business Information Section */}
              <div className="space-y-1">
                <div className="flex items-center space-x-2 mb-1">
                  <div className="flex-shrink-0 w-6 h-6 bg-brand rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-xs">2</span>
                  </div>
                  <h3 className="text-lg font-bold text-ui-text relative top-1">
                    {t("register.sections.business")}
                  </h3>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <InputField
                    label={t("register.fields.businessName")}
                    value={formData.business_name}
                    ref={businessNameRef}
                    onChange={(e) =>
                      handleFieldChange("business_name", e.target.value)
                    }
                    error={errors.business_name}
                    placeholder={t("register.fields.businessNameShortPh")}
                    icon={<BriefcaseIcon />}
                    required
                  />
                  <InputField
                    label={t("register.fields.industry")}
                    value={formData.industry_type}
                    ref={industryTypeRef}
                    onChange={(e) =>
                      handleFieldChange("industry_type", e.target.value)
                    }
                    error={errors.industry_type}
                    placeholder={t("register.fields.industryShortPh")}
                    icon={<BriefcaseIcon />}
                    required
                  />
                </div>
              </div>

              {/* Personal Information Section */}
              <div className="space-y-1">
                <div className="flex items-center space-x-2 mb-1">
                  <div className="flex-shrink-0 w-6 h-6 bg-brand rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-xs">3</span>
                  </div>
                  <h3 className="text-lg font-bold text-ui-text relative top-1">
                    {t("register.sections.personal")}
                  </h3>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <InputField
                    label={t("register.fields.firstName")}
                    value={formData.firstname}
                    ref={firstnameRef}
                    onChange={(e) =>
                      handleFieldChange("firstname", e.target.value)
                    }
                    error={errors.firstname}
                    placeholder={t("register.fields.firstNamePh")}
                    icon={<UserIcon />}
                    required
                  />
                  <InputField
                    label={t("register.fields.lastName")}
                    value={formData.lastname}
                    ref={lastnameRef}
                    onChange={(e) =>
                      handleFieldChange("lastname", e.target.value)
                    }
                    error={errors.lastname}
                    placeholder={t("register.fields.lastNamePh")}
                    icon={<UserIcon />}
                    required
                  />
                </div>
              </div>

              {/* Account Information Section */}
              <div className="space-y-1">
                <div className="flex items-center space-x-2 mb-1">
                  <div className="flex-shrink-0 w-6 h-6 bg-brand rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-xs">4</span>
                  </div>
                  <h3 className="text-lg font-bold text-ui-text relative top-1">
                    {t("register.sections.account")}
                  </h3>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <InputField
                    label={t("register.fields.username")}
                    value={formData.username}
                    ref={usernameRef}
                    onChange={(e) =>
                      handleFieldChange("username", e.target.value)
                    }
                    error={errors.username}
                    placeholder={t("register.fields.usernamePh")}
                    icon={<UserIcon />}
                    required
                  />
                  <InputField
                    label={t("register.fields.email")}
                    type="email"
                    value={formData.email}
                    ref={emailRef}
                    onChange={(e) => handleFieldChange("email", e.target.value)}
                    error={errors.email}
                    placeholder={t("register.fields.emailPh")}
                    icon={<EnvelopeIcon />}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <InputField
                    label={t("register.fields.password")}
                    type="password"
                    value={formData.password}
                    ref={passwordRef}
                    onChange={(e) =>
                      handleFieldChange("password", e.target.value)
                    }
                    error={errors.password}
                    placeholder={t("register.fields.passwordPh")}
                    required
                  />
                  <InputField
                    label={t("register.fields.confirmPassword")}
                    type="password"
                    value={confirmPassword}
                    onChange={(e) =>
                      handleConfirmPasswordChange(e.target.value)
                    }
                    error={errors.confirmPassword}
                    placeholder={t("register.fields.confirmPasswordPh")}
                    required
                  />
                </div>
              </div>

              {/* Terms & Verification Section */}
              <div className="space-y-1">
                <div className="flex items-center space-x-2 mb-1">
                  <div className="flex-shrink-0 w-6 h-6 bg-brand rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-xs">5</span>
                  </div>
                  <h3 className="text-lg font-bold text-ui-text relative top-1">
                    Conditions et vérification / Terms & Verification
                  </h3>
                </div>
                <div className="bg-ui-surface-sunken rounded-lg p-4 space-y-3">
                  <LegalAgreement
                    value={legal}
                    onChange={handleLegalChange}
                    errors={errors}
                  />
                </div>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-shrink-0">
                    <ReCAPTCHA
                      sitekey="6Ld9BMcjAAAAAE-VKFPaecDHGEqeSKQdQ_GsZ-0W"
                      onChange={onRecaptchaChange}
                      ref={recaptchaRef}
                      size="normal"
                      theme="light"
                    />
                    {errors.recaptcha && (
                      <p className="text-state-danger-text text-xs mt-1 flex items-center">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {errors.recaptcha}
                      </p>
                    )}
                  </div>
                  <div className="flex-grow max-w-md">
                    <button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="w-full bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold py-3 px-6 rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand"
                    >
                      {loading ? <BtnLoader /> : t("register.createAccount")}
                    </button>
                    <p className="text-center text-xs text-ui-text-muted mt-2">
                      {t("register.haveAccount")}{" "}
                      <Link
                        to="/login/warehouse"
                        className="text-brand hover:text-brand-hover font-semibold ml-1"
                      >
                        {t("register.signInHere")}
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-ui-text-muted">
            {t("register.supportLine")}{" "}
            <a
              href="mailto:info@hajexbolt.com"
              className="text-brand hover:text-brand-hover font-semibold"
            >
              info@hajexbolt.com
            </a>
          </p>
          <LegalFooter className="mt-3" />
        </div>
      </div>
    </div>
  );
};

export default WarehouseForm;
